import {Extension, Mark, Node} from '@tiptap/core';
import Blockquote from '@tiptap/extension-blockquote';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import {UndoRedo} from '@tiptap/extensions';
import {BulletList, ListItem, ListKeys, OrderedList} from './rich-text-lists';

/**
 * The schema of the panel's rich-text editor. Stored texts are HTML that CKEditor 4 wrote (and
 * people pasted), shown again on customer sites with innerHTML, so the editor keeps what it loads
 * as it is: every inline element (span, a, b, strong, font, ...) is one mark that remembers its tag
 * and its attributes exactly as written (the raw style string too), and nests in the order it was
 * written. Formatting from the toolbar is written the way CKEditor wrote it (see rich-text-commands).
 *
 * Two document shapes:
 * - `inline`: the widget texts. No paragraphs: a line break is `<br>`, and the text is the document
 *   itself (CKEditor's enterMode BR with autoParagraph off).
 * - `blocks`: paragraph-based fields (sb-textarea, the influencer profile): paragraphs, headings,
 *   lists (rich-text-lists), quotes and rules, each keeping its own attributes.
 *
 * What neither shape holds (block elements in an inline text, a table, a comment, a style element)
 * does not survive loading; the editor checks that before it edits (rich-text-html, sameHtml).
 */

/** Inline elements kept as a mark. */
export const INLINE_TAGS = [
  'span', 'a', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'del', 'ins', 'sup', 'sub', 'small', 'big',
  'font', 'mark', 'code', 'abbr', 'cite', 'q', 'kbd', 'samp', 'var', 'dfn', 'time', 'bdi', 'bdo',
];

export type HtmlAttributes = Record<string, string>;

/** An element's attributes in the order they were written, values untouched. */
export const attributesOf = (element: Element, except: string[] = []): HtmlAttributes => {
  const attributes: HtmlAttributes = {};
  for (const attribute of Array.from(element.attributes)) {
    if (!except.includes(attribute.name)) {
      attributes[attribute.name] = attribute.value;
    }
  }
  return attributes;
};

/**
 * Every inline element: `tag` and `attributes` as written. `excludes: ''` lets any number of them
 * stack, and as they share one mark type ProseMirror keeps them in the order they were parsed
 * (outermost first), so `<span><a>` and `<a><span>` both come back as they were.
 */
export const HtmlInline = Mark.create({
  name: 'htmlInline',
  excludes: '',
  priority: 1000,

  addAttributes() {
    return {
      tag: {default: 'span', parseHTML: (element) => element.tagName.toLowerCase(), renderHTML: () => ({})},
      attributes: {default: {}, parseHTML: (element) => attributesOf(element), renderHTML: () => ({})},
    };
  },

  parseHTML() {
    return INLINE_TAGS.map((tag) => ({tag}));
  },

  renderHTML({mark}) {
    return [mark.attrs['tag'], {...mark.attrs['attributes']}, 0];
  },
});

/** `<img>` as written. */
export const HtmlImage = Node.create({
  name: 'htmlImage',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      attributes: {default: {}, parseHTML: (element) => attributesOf(element), renderHTML: () => ({})},
    };
  },

  parseHTML() {
    return [{tag: 'img'}];
  },

  renderHTML({node}) {
    return ['img', {...node.attrs['attributes']}];
  },
});

/** The block nodes' own attributes (style, class, id, align, dir, ...), kept as written. */
const BlockAttributes = Extension.create({
  name: 'htmlBlockAttributes',

  addGlobalAttributes() {
    return [{
      types: ['paragraph', 'heading', 'bulletList', 'orderedList', 'listItem', 'blockquote', 'horizontalRule'],
      attributes: {
        htmlAttributes: {
          default: {},
          parseHTML: (element) => attributesOf(element),
          renderHTML: (attributes) => ({...attributes['htmlAttributes']}),
        },
      },
    }];
  },
});

/** The document of a widget text: text, marks, line breaks and images, no blocks. */
const InlineDocument = Document.extend({content: 'inline*'});

export type RichTextShape = 'inline' | 'blocks';

export const richTextExtensions = (shape: RichTextShape) => {
  const common = [Text, HtmlInline, HtmlImage, HardBreak.configure({keepMarks: true}), UndoRedo];
  if (shape === 'inline') {
    return [InlineDocument, ...common];
  }
  return [
    Document,
    Paragraph,
    Heading,
    BulletList,
    OrderedList,
    ListItem,
    ListKeys,
    Blockquote,
    HorizontalRule,
    BlockAttributes,
    ...common,
  ];
};
